import React from "react";
import { styled } from "@mui/material/styles";
import { css } from "@emotion/react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Guard from "components/Guard/Guard";
import { ApplicationHistoryFieldDisplayName } from "models/Application.model";
import { parseChangeListItem } from "./functions/parseChangeListItem";

type Props = {
  displayName: string;
  before: any;
  after: any;
};

const ListItem: React.FC = ({ children }) => {
  return (
    <StyledListItem>
      <Typography variant="body2">{children}</Typography>
    </StyledListItem>
  );
};

const ParticipantChangeListItem: React.FC<Props> = ({ displayName, before, after }) => {
  const { t } = useTranslation();

  const isQualifications = displayName === ApplicationHistoryFieldDisplayName.qualifications;
  const isParticipantTypes = displayName === ApplicationHistoryFieldDisplayName.participantTypes;

  const [beforeValue, afterValue] = React.useMemo(() => parseChangeListItem(before, after, displayName), [
    before,
    after,
    displayName,
  ]);

  if (beforeValue && !afterValue) {
    // Remove value
    return (
      <ListItem>
        <Header>{t(`${displayName} removed: `)}</Header>
        <Guard condition={isQualifications || isParticipantTypes}>
          <br />
        </Guard>
        {beforeValue}
      </ListItem>
    );
  } else if (!beforeValue && afterValue) {
    // Add value
    return (
      <ListItem>
        <Header>{t(`${displayName} added: `)}</Header>
        <Guard condition={isQualifications || isParticipantTypes}>
          <br />
        </Guard>
        {afterValue}
      </ListItem>
    );
  }

  // Update value
  return (
    <ListItem>
      <Header>{t(`${displayName}: `)}</Header>

      <Guard condition={isQualifications || isParticipantTypes} fallback={`${beforeValue} → ${afterValue}`}>
        <br />
        <SubHeader>{t(isQualifications ? "From: " : "Removed: ")}</SubHeader>
        {beforeValue}
        <br />
        <SubHeader>{t(isQualifications ? "To: " : "Added: ")}</SubHeader>
        {afterValue}
      </Guard>
    </ListItem>
  );
};

const Header = styled("strong")(
  ({ theme }) => css`
    font-weight: 600;
  `
);

const SubHeader = styled("strong")(
  ({ theme }) => css`
    font-weight: 500;
  `
);

const StyledListItem = styled("li")(
  ({ theme }) => css`
    margin-bottom: 6px;
  `
);

export default ParticipantChangeListItem;
